import { useState, useEffect, useCallback } from 'react';
import { matchVideosService } from '../services/matchVideosService';
import { generateStorageThumbnailUrl } from '../utils/thumbnailUtils';
import { detectVideoSource, extractYouTubeVideoId } from '../components/video/utils';

interface MatchVideo {
  id: string;
  match_id: string;
  video_url: string;
  thumbnail_url: string | null;
  video_order: number;
  video_source_type?: 'youtube' | 'cloudflare_r2' | 'supabase_storage' | string;
  storage_video_id?: string | null;
  created_at: string;
  updated_at: string;
  created_by: string;
  updated_by: string;
}

interface MatchVideosData {
  [matchId: string]: MatchVideo[];
}

// Cache for thumbnails to avoid repeated fetching
const thumbnailCache = new Map<string, string | null>();
const CACHE_KEY = 'match_videos_cache';

export const useMatchVideos = (matchIds: string[]) => {
  const [matchVideos, setMatchVideos] = useState<MatchVideosData>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Helper function to generate thumbnail URL from video URL
  const generateThumbnailFromVideoUrl = (videoUrl: string): string | null => {
    if (!videoUrl) return null;
    
    const source = detectVideoSource(videoUrl);
    
    // For storage videos (R2, Supabase Storage), generate thumbnail URL from pattern
    if (source === 'cloudflare_r2' || source === 'supabase_storage') {
      return generateStorageThumbnailUrl(videoUrl, 0);
    }
    
    // For YouTube videos, use YouTube thumbnail API
    if (source === 'youtube') {
      const videoId = extractYouTubeVideoId(videoUrl);
      if (videoId) {
        return `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
      }
    }
    
    return null;
  };

  // Load cached data on mount
  useEffect(() => {
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      if (cached) {
        const cachedData = JSON.parse(cached);
        setMatchVideos(cachedData);
        
        // Populate thumbnail cache - generate from video_url instead of using thumbnail_url
        Object.entries(cachedData).forEach(([matchId, videos]: [string, any]) => {
          if (videos && videos.length > 0) {
            const firstVideo = videos.find((video: any) => video.video_order === 1) || videos[0];
            const generatedThumbnail = generateThumbnailFromVideoUrl(firstVideo.video_url);
            thumbnailCache.set(matchId, generatedThumbnail);
          }
        });
      }
    } catch (err) {
      console.warn('Failed to load cached match videos:', err);
    }
  }, []);

  // Save to cache when data changes
  useEffect(() => {
    if (Object.keys(matchVideos).length > 0) {
      try {
        localStorage.setItem(CACHE_KEY, JSON.stringify(matchVideos));
      } catch (err) {
        console.warn('Failed to cache match videos:', err);
      }
    }
  }, [matchVideos]);

  // Manual fetch function - only call when needed
  const fetchMatchVideos = useCallback(async (matchIdsToFetch: string[]) => {
    if (matchIdsToFetch.length === 0) return;

    setLoading(true);
    setError(null);

    try {
      const videoPromises = matchIdsToFetch.map(async (matchId) => {
        const result = await matchVideosService.getMatchVideos(matchId);
        return { matchId, videos: result.success ? result.data : [] };
      });

      const results = await Promise.all(videoPromises);
      
      const newVideosData: MatchVideosData = {};
      results.forEach(({ matchId, videos }) => {
        newVideosData[matchId] = videos || [];
        
        if (videos && videos.length > 0) {
          const firstVideo = videos.find(video => video.video_order === 1) || videos[0];
          
          // Generate thumbnail URL from video_url instead of using thumbnail_url from DB
          // This bypasses RLS issues and uses the predictable pattern
          const generatedThumbnail = generateThumbnailFromVideoUrl(firstVideo.video_url);
          thumbnailCache.set(matchId, generatedThumbnail);
        } else {
          thumbnailCache.set(matchId, null);
        }
      });

      setMatchVideos(prev => ({ ...prev, ...newVideosData }));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch match videos');
    } finally {
      setLoading(false);
    }
  }, []);

  const getMatchThumbnail = (matchId: string): string | null => {
    // Check cache first
    if (thumbnailCache.has(matchId)) {
      return thumbnailCache.get(matchId) || null;
    }
    
    // Fallback to state - generate from video_url
    const videos = matchVideos[matchId];
    if (!videos || videos.length === 0) return null;
    
    // Generate thumbnail URL from video_url instead of using thumbnail_url from DB
    const firstVideo = videos.find(video => video.video_order === 1) || videos[0];
    const generatedThumbnail = generateThumbnailFromVideoUrl(firstVideo.video_url);
    
    // Cache it for future use
    if (generatedThumbnail) {
      thumbnailCache.set(matchId, generatedThumbnail);
    }
    
    return generatedThumbnail;
  };

  const getMatchVideoCount = (matchId: string): number => {
    const videos = matchVideos[matchId];
    return videos ? videos.length : 0;
  };

  // Clear cache function
  const clearCache = useCallback(() => {
    thumbnailCache.clear();
    localStorage.removeItem(CACHE_KEY);
    setMatchVideos({});
  }, []);

  return {
    matchVideos,
    loading,
    error,
    getMatchThumbnail,
    getMatchVideoCount,
    fetchMatchVideos,
    clearCache
  };
};
